<template>
  <div class="h-full flex gap-4">
    <!-- 섹션 1: 필터/선택 영역 -->
    <div class="w-[400px] flex-shrink-0 pl-6 py-6 flex flex-col gap-4">
      <!-- 날짜 선택 -->
      <DateSelector v-model="selectedDate" class="flex-shrink-0" />
      <!-- 내 바스켓 -->
      <div class="bg-white rounded-lg shadow p-4">
        <div class="flex items-center justify-between mb-3">
          <h3 class="text-sm font-medium text-gray-700">내 바스켓</h3>
          <div v-if="userBaskets.length > 0" class="flex items-center gap-2">
            <span class="text-xs text-gray-500">{{ userBaskets.length }}/5</span>
            <button @click="openCreateBasketModal" :disabled="userBaskets.length >= 5"
              class="text-xs bg-blue-500 hover:bg-blue-600 disabled:bg-gray-300 text-white px-2 py-1 rounded">
              + 추가
            </button>
          </div>
        </div>

        <!-- 바스켓 목록 -->
        <!-- 디버그: {{ userBaskets }} -->
        <div v-if="userBaskets.length === 0" class="text-center py-8 text-gray-500">
          <svg xmlns="http://www.w3.org/2000/svg" class="h-12 w-12 mx-auto mb-3 text-gray-400" fill="none"
            viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
              d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
          </svg>
          <p class="text-sm mb-2">아직 바스켓이 없습니다</p>
          <p class="text-xs text-gray-400 mb-4">관심 있는 종목들을 모아<br />나만의 바스켓을 만들어보세요!</p>
          <button @click="openCreateBasketModal" :disabled="userBaskets.length >= 5"
            class="bg-blue-500 hover:bg-blue-600 disabled:bg-gray-300 text-white py-2 px-4 rounded-md text-xs font-medium">
            바스켓 만들기
          </button>
        </div>

        <div v-else class="space-y-2 mb-4">
          <div v-for="basket in userBaskets" :key="basket.id" @click="selectBasket(basket.id)" :class="[
            'p-3 border rounded-lg cursor-pointer transition-all',
            selectedBasketIds.includes(basket.id.toString())
              ? 'border-blue-500 bg-blue-50'
              : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
          ]">
            <div class="flex items-center justify-between">
              <div class="flex-1">
                <h4 class="text-sm font-medium text-gray-800">{{ basket.name }}</h4>
                <p class="text-xs text-gray-500">{{ getBasketItemCount(basket.id) }}개 종목</p>
              </div>
              <div class="flex items-center gap-1">
                <button @click.stop="editBasket(basket)" class="p-1 text-gray-400 hover:text-gray-600">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24"
                    stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                      d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                </button>
                <button @click.stop="deleteBasket(basket)" class="p-1 text-gray-400 hover:text-red-500">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24"
                    stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                      d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>


      </div>


      <!-- 국고채 유형 선택 -->
      <KtbTypeComponent v-model="selectedKtbTypes" />
    </div>

    <!-- 섹션 2: 채권 목록 -->
    <div class="w-[35%] py-6">
      <div class="mb-4 px-6 flex items-center gap-3">
        <button @click="hideEvaluatedYield = !hideEvaluatedYield" :class="[
          'px-3 py-1.5 rounded-md text-xs font-medium transition-colors',
          hideEvaluatedYield
            ? 'bg-gray-600 text-white shadow-sm'
            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
        ]">
          {{ hideEvaluatedYield ? '민평 표시' : '민평 숨기기' }}
        </button>
      </div>
      <BondListComponent :selected-date="selectedDate" :selected-baskets="selectedBasketsData"
        :selected-bond-codes="manuallySelectedBonds" :sort-by="sortBy" :sort-direction="sortDirection"
        :hide-evaluated-yield="hideEvaluatedYield" :updated-bond-code="lastUpdatedBondCode"
        :all-bonds-data="allBondsForModal" @select-bond="selectBond" @toggle-sort="toggleSort"
        @toggle-selected-bond="toggleSelectedBond" />
    </div>

    <!-- 섹션 3: 선택된 채권 상세 정보 + 채팅 목록 -->
    <div class="flex-1 py-6 pr-6">
      <div class="bg-white rounded-lg shadow h-full flex flex-col overflow-hidden" ref="chatContainer">
        <!-- 검색 기능 추가 -->
        <div class="flex-shrink-0 p-4 border-b">
          <div class="flex items-center gap-2">
            <input type="text" v-model="searchBondInput" placeholder="종목 입력 (예: 2407)"
              class="flex-1 px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              @keyup.enter="searchBondByCode" />
            <button @click="searchBondByCode" class="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-md">
              검색
            </button>
          </div>
        </div>

        <!-- 선택된 채권 상세 정보 (이전 섹션 3) -->
        <BondDetailComponent v-if="selectedBond" :bond="selectedBond" @copy-to-clipboard="copyToClipboard" />

        <div v-else class="flex-shrink-0 border-b p-4 text-center text-gray-500">
          <p>선택된 채권이 없습니다</p>
          <p class="text-sm mt-2">좌측 목록에서 채권을 선택해주세요</p>
        </div>

        <!-- 채팅 목록 영역 -->
        <ChatListComponent v-if="selectedBondName" :chat-list="filteredChatList" :bond-name="selectedBondName"
          :selected-bond="selectedBond" :has-new-quote="hasNewQuote" @clear-new-quote-alert="clearNewQuoteAlert" />
      </div>
    </div>
  </div>

  <!-- 모달: 바스켓 생성/수정 (새로운 좌우분할 디자인) -->
  <div v-if="showSaveModal" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
    <div class="bg-white rounded-lg shadow-xl w-[1000px] h-[700px] flex flex-col">
      <!-- 모달 헤더 -->
      <div class="px-6 py-4 border-b flex justify-between items-center">
        <div>
          <h3 class="text-lg font-medium text-gray-800">{{ isEditMode ? '바스켓 수정' : '새 바스켓 만들기' }}</h3>
          <p class="text-sm text-gray-500 mt-1">좌측에서 종목을 선택하고 우측으로 드래그하여 바스켓을 구성하세요</p>
        </div>
        <button @click="closeSaveModal" class="text-gray-400 hover:text-gray-600">
          <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <!-- 바스켓 이름 입력 -->
      <div class="px-6 py-3 border-b bg-gray-50">
        <label for="basketName" class="block text-sm font-medium text-gray-700 mb-2">바스켓 이름</label>
        <input type="text" id="basketName" v-model="newGroupName" placeholder="바스켓 이름을 입력하세요"
          class="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm" />
      </div>

      <!-- 모달 메인 콘텐츠 (좌우분할) -->
      <div class="flex-1 flex overflow-hidden">
        <!-- 좌측: 전체 종목 목록 -->
        <div class="w-1/2 border-r flex flex-col">
          <div class="px-4 py-3 border-b bg-gray-50">
            <h4 class="text-sm font-medium text-gray-700 mb-2">전체 종목</h4>
            <!-- 검색 입력 -->
            <div class="relative">
              <input type="text" v-model="bondSearchQuery" placeholder="종목명으로 검색..."
                class="w-full px-3 py-2 pl-8 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-xs" />
              <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 absolute left-2.5 top-2.5 text-gray-400"
                fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
          </div>

          <div class="flex-1 overflow-y-auto">
            <div v-for="bond in filteredAllBonds" :key="bond.code" :draggable="true" @dragstart="handleDragStart(bond)"
              @dragend="handleDragEnd" :class="[
                'p-3 border-b cursor-move hover:bg-blue-50 transition-colors',
                modalSelectedBonds.includes(bond.code) ? 'bg-gray-100 opacity-50' : ''
              ]">
              <div class="flex items-center justify-between">
                <div class="flex-1">
                  <div class="flex items-center gap-2">
                    <span class="text-xs px-2 py-1 bg-blue-100 text-blue-800 rounded">{{ getTypeLabel(bond.ktbType ||
                      bond.msbType) }}</span>
                    <span class="text-sm font-medium">{{ formatBondName(bond.alias || bond.bondName) }}</span>
                  </div>
                  <div class="text-xs text-gray-500 mt-1">
                    {{ bond.code }}
                  </div>
                </div>
                <button v-if="!modalSelectedBonds.includes(bond.code)" @click="addBondToModal(bond.code)"
                  class="p-1 text-blue-500 hover:text-blue-700">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24"
                    stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                      d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- 우측: 선택된 종목 (바스켓 구성) -->
        <div class="w-1/2 flex flex-col">
          <div class="px-4 py-3 border-b bg-blue-50">
            <h4 class="text-sm font-medium text-gray-700">
              선택된 종목 ({{ modalSelectedBonds.length }}개)
            </h4>
          </div>

          <div class="flex-1 overflow-y-auto" @dragover.prevent @drop="handleDrop">
            <div v-if="modalSelectedBonds.length === 0" class="flex items-center justify-center h-full text-gray-500">
              <div class="text-center">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-12 w-12 mx-auto mb-3 text-gray-400" fill="none"
                  viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                    d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                </svg>
                <p class="text-sm mb-2">종목을 선택해주세요</p>
                <p class="text-xs text-gray-400">좌측에서 종목을 드래그하거나<br />+ 버튼을 클릭하세요</p>
              </div>
            </div>

            <div v-else>
              <div v-for="(code, index) in modalSelectedBonds" :key="code"
                class="p-3 border-b hover:bg-gray-50 transition-colors">
                <div class="flex items-center justify-between">
                  <div class="flex-1">
                    <div class="flex items-center gap-2">
                      <span class="text-xs px-2 py-1 bg-green-100 text-green-800 rounded">{{ getBondTypeByCode(code)
                      }}</span>
                      <span class="text-sm font-medium">{{ formatBondName(getBondNameByCode(code)) }}</span>
                    </div>
                    <div class="text-xs text-gray-500 mt-1">
                      {{ code }}
                    </div>
                  </div>
                  <div class="flex items-center gap-1">
                    <span class="text-xs text-gray-400">{{ index + 1 }}</span>
                    <button @click="removeBondFromModal(code)" class="p-1 text-red-500 hover:text-red-700">
                      <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24"
                        stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                          d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 모달 푸터 -->
      <div class="px-6 py-4 border-t flex justify-between items-center">
        <div class="text-sm text-gray-500">
          {{ modalSelectedBonds.length }}개 종목 선택됨
        </div>
        <div class="flex gap-3">
          <button @click="closeSaveModal"
            class="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-md text-sm font-medium">
            취소
          </button>
          <button @click="saveGroup" :disabled="!newGroupName.trim() || modalSelectedBonds.length === 0" :class="[
            'px-4 py-2 rounded-md text-sm font-medium',
            newGroupName.trim() && modalSelectedBonds.length > 0
              ? 'bg-blue-500 hover:bg-blue-600 text-white'
              : 'bg-gray-300 text-gray-500 cursor-not-allowed',
          ]">
            {{ isEditMode ? '수정 완료' : '바스켓 생성' }}
          </button>
        </div>
      </div>
    </div>
  </div>

  <!-- 클립보드 복사 알림 -->
  <div v-if="showCopyNotification"
    class="fixed bottom-4 right-4 bg-gray-800 text-white px-4 py-2 rounded-md shadow-lg z-50 flex items-center">
    <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 mr-2 text-green-400" fill="none" viewBox="0 0 24 24"
      stroke="currentColor">
      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
    </svg>
    코드가 클립보드에 복사되었습니다
  </div>
</template>

<script>
import { ref, onMounted, onUnmounted, watch, computed } from 'vue';
import { onBeforeRouteLeave } from 'vue-router';
import DateSelector from '@/components/NavigationDateSelector.vue';
import KtbTypeComponent from '@/components/KtbTypeComponent.vue';
import BondListComponent from '@/components/BondListComponent.vue';
import BondDetailComponent from '@/components/BondDetailComponent.vue';
import ChatListComponent from '@/components/ChatListComponent.vue';
import ktbService from '@/service/ktbService';
import basketService from '@/service/basketService';
import userService from '@/service/userService';
import { getTodayStr } from '@/utils/util';
import { format, differenceInMonths } from 'date-fns';
import sseManager from '@/utils/sseManager';

const BOND_GROUPS_STORAGE_KEY = 'ktb-bond-groups';
const BASKET_SELECTION_STORAGE_KEY = 'nonbenchmark-basket-selection';
const KTB_SELECTION_STORAGE_KEY = 'nonbenchmark-ktb-selection';
const MANUAL_BONDS_STORAGE_KEY = 'nonbenchmark-manual-bonds';

export default {
  name: 'NonBenchmarkView',
  components: {
    DateSelector,
    KtbTypeComponent,
    BondListComponent,
    BondDetailComponent,
    ChatListComponent,
  },
  setup() {
    // State
    const selectedDate = ref(getTodayStr());
    const selectedKtbTypes = ref([]); // 기본값을 빈 배열로 변경
    const selectedBondCodes = ref([]); // 선택된 특정 본드 코드들
    const ktbBonds = ref({});
    const ktbQuotes = ref(new Map()); // KTB 견적 데이터 - Map 구조로 code를 키로 사용
    const chatList = ref([]);
    const selectedBondName = ref('');
    const selectedBond = ref(null);
    const isLoading = ref(false);
    const chatContainer = ref(null);
    const searchBondInput = ref(''); // 검색 입력 상태 추가
    const hasNewQuote = ref(false); // 새 호가 알림 상태


    // 바스켓 관련 상태
    const userBaskets = ref([]);
    const selectedBasketIds = ref([]); // 복수 바스켓 선택
    const manuallySelectedBonds = ref([]); // 1번섹션에서 수동 선택된 채권들

    // 레거시 지원을 위한 selectedBondsForGroup (BondListComponent에서 사용)
    const selectedBondsForGroup = computed(() => {
      // 더 이상 사용하지 않음 - 개별 채권과 바스켓 그룹으로 분리
      return [];
    });

    // 기존 localStorage 그룹 관련 상태 (호환성을 위해 유지)
    const savedGroups = ref([]);
    const activeGroupIndex = ref(-1);

    // 정렬 관련 상태
    const sortBy = ref('maturityDate');
    const sortDirection = ref('asc');
    // 채팅 필터링 옵션
    const hideFragmentTrades = ref(false);
    const hideWithoutPrice = ref(false);

    // 그룹별 보기 옵션
    const isGroupedView = ref(false);
    // 민평 숨기기 옵션
    const hideEvaluatedYield = ref(false);

    // SSE 연결 관리 (하나의 연결로 통합)
    const sseEventSource = ref(null);
    const lastUpdatedBondCode = ref(null); // { code: string, timestamp: number }
    const isSSEConnecting = ref(false); // SSE 연결 중 상태 관리



    // 선택된 채권 타입 (국고채/통안채)
    const selectedBondType = ref('KTB'); // 기본값 국고채

    // 페이지 가시성 관련 변수들
    let healthCheckInterval = null;
    const HEALTH_CHECK_INTERVAL = 5000; // 5초

    // KTB 채권 기본 정보 로드 함수
    const loadKtbBonds = async () => {
      try {
        const data = await ktbService.findAllKtbBonds();

        // 새로운 API 응답을 maturityRangeType별로 그룹화
        const groupedData = {};
        data.forEach(bond => {
          const type = bond.maturityRangeType;
          if (!groupedData[type]) {
            groupedData[type] = [];
          }
          groupedData[type].push({
            code: bond.code,
            bondName: bond.alias, // alias를 bondName으로 사용
            alias: bond.alias,
            maturityRangeType: bond.maturityRangeType,
            maturityTypeName: bond.maturityTypeName,
            isBenchmark: bond.isBenchmark,
            maturityDate: bond.maturityDate, // API에서 제공하는 실제 만기일
            // 기본값 설정 (Quote 데이터에서 실제 값으로 업데이트됨)
            buyPrice: 'N/A',
            sellPrice: 'N/A',
            yield: 'N/A',
            volume: 'N/A',
            coupon: 'N/A'
          });
        });

        ktbBonds.value = groupedData;
      } catch (error) {
        console.error('Failed to fetch KTB bonds:', error);
      }
    };

    // KTB 견적 데이터 로드 함수
    const loadKtbQuotes = async () => {
      try {
        isLoading.value = true;
        const data = await ktbService.findKtbQuotes(selectedDate.value);
        const quotesArray = Array.isArray(data) ? data : [];

        // 배열을 Map으로 변환 (code를 키로 사용)
        const quotesMap = new Map();
        quotesArray.forEach(quote => {
          if (quote.code) {
            quotesMap.set(quote.code, quote);
          }
        });

        ktbQuotes.value = quotesMap;
      } catch (error) {
        console.error('Failed to fetch KTB quotes:', error);
        ktbQuotes.value = new Map();
      } finally {
        isLoading.value = false;
      }
    };



    // 초기 데이터 로드
    const loadInitialData = async () => {
      try {
        isLoading.value = true;
        // 병렬로 데이터 로드
        await Promise.all([loadKtbBonds(), loadKtbQuotes()]);

      } catch (error) {
        console.error('데이터 로드 실패:', error);
      } finally {
        isLoading.value = false;
      }
    };

    // SSE 연결을 별도로 처리하는 함수
    const initializeSSEConnection = () => {
      const username = localStorage.getItem('username');
      if (username) {
        connectSSEStream(selectedDate.value, username);
      }
    };

    // 페이지 가시성 변경 감지 핸들러
    const handleVisibilityChange = () => {
      if (document.hidden) {
        // 페이지가 숨겨질 때 인터벌 중지
        if (healthCheckInterval) {
          clearInterval(healthCheckInterval);
          healthCheckInterval = null;
        }
      } else {
        // 페이지가 보일 때 인터벌 시작
        startHealthCheck();
      }
    };

    // health check 시작 함수
    const startHealthCheck = () => {
      if (healthCheckInterval) return;

      // 즉시 한 번 실행
      sendHealthCheck();

      // 이후 주기적으로 실행
      healthCheckInterval = setInterval(() => {
        sendHealthCheck();
      }, HEALTH_CHECK_INTERVAL);
    };

    // health check 전송 함수
    const sendHealthCheck = async () => {
      if (!document.hidden) {
        try {
          const currentPath = window.location.pathname;
          await userService.sendHealthCheck(currentPath);
        } catch (error) {
          console.error('Health check 전송 실패:', error);
        }
      }
    };

    // 필터링된 KTB 유형 - 선택된 유형만 표시
    const filteredKtbTypes = computed(() => {
      if (typeof selectedKtbTypes.value === 'string') {
        return [selectedKtbTypes.value];
      }

      if (Array.isArray(selectedKtbTypes.value)) {
        return selectedKtbTypes.value;
      }

      // 객체 형태인 경우 (본드 선택이 포함된 경우)
      if (
        selectedKtbTypes.value &&
        typeof selectedKtbTypes.value === 'object'
      ) {
        return selectedKtbTypes.value.types || [];
      }

      return [];
    });


    // KTB 채권 데이터와 견적 데이터를 결합하여 처리
    const processedKtbBonds = computed(() => {
      const result = [];

      // selectedKtbTypes가 객체 형태이고 bonds 배열이 있는 경우 (개별 선택)
      if (selectedKtbTypes.value && typeof selectedKtbTypes.value === 'object' && selectedKtbTypes.value.bonds) {
        const selectedBondCodes = selectedKtbTypes.value.bonds;

        // 선택된 채권 코드만 처리
        for (const typeId in ktbBonds.value) {
          const bonds = ktbBonds.value[typeId] || [];
          bonds.forEach(bond => {
            // 선택된 채권 코드에 포함된 경우만 추가
            if (selectedBondCodes.includes(bond.code)) {
              const quote = ktbQuotes.value.get(bond.code);

              result.push({
                ...bond,
                bondType: 'KTB',
                ktbType: typeId,
                name: quote?.name || bond.alias,
                evaluatedYield: quote?.evaluatedYield || null,
                bidYield: quote?.bidYield || null,
                askYield: quote?.askYield || null,
                totalTradingVolume: quote?.totalTradingVolume || 0,
                maturityDate: bond.maturityDate,
                yield: quote?.evaluatedYield || 'N/A',
                buyPrice: quote?.bidYield?.yield || 'N/A',
                sellPrice: quote?.askYield?.yield || 'N/A',
                volume: quote?.totalTradingVolume ? (quote.totalTradingVolume / 100000000).toFixed(0) : '0',
              });
            }
          });
        }
      } else {
        // 기존 로직: 선택된 국고채 유형에 해당하는 모든 본드 처리
        for (const typeId in ktbBonds.value) {
          if (filteredKtbTypes.value.includes(typeId)) {
            const bonds = ktbBonds.value[typeId] || [];
            bonds.forEach(bond => {
              const quote = ktbQuotes.value.get(bond.code);

              result.push({
                ...bond,
                bondType: 'KTB',
                ktbType: typeId,
                name: quote?.name || bond.alias,
                evaluatedYield: quote?.evaluatedYield || null,
                bidYield: quote?.bidYield || null,
                askYield: quote?.askYield || null,
                bidAt: quote?.bidAt || null,
                askAt: quote?.askAt || null,
                totalTradingVolume: quote?.totalTradingVolume || 0,
                maturityDate: bond.maturityDate,
                yield: quote?.evaluatedYield || 'N/A',
                buyPrice: quote?.bidYield?.yield || 'N/A',
                sellPrice: quote?.askYield?.yield || 'N/A',
                volume: quote?.totalTradingVolume ? (quote.totalTradingVolume / 100000000).toFixed(0) : '0',
              });
            });
          }
        }
      }

      return result;
    });

    // MSB 채권 데이터 처리 (현재는 빈 배열로 처리)
    const msbBonds = ref([]);
    const selectedMsbTypes = ref([]);
    const filteredMsbTypes = computed(() => []);

    const processedMsbBonds = computed(() => {
      // 현재는 MSB 데이터가 없으므로 빈 배열 반환
      return [];
    });

    // 전체 채권 목록 (국고채 + 통안채)
    const allBonds = computed(() => {

      const result = [...processedKtbBonds.value, ...processedMsbBonds.value];

      return result;
    });

    // 2번섹션에 표시될 개별 채권 목록 (바스켓 제외, 수동선택만)
    const displayedBonds = computed(() => {
      // 1번섹션에서 수동 선택된 채권들만 표시 (중복 허용)
      const individualBonds = [];

      manuallySelectedBonds.value.forEach((bondCode, index) => {
        // 수동 선택된 채권도 전체 데이터에서 찾기
        const bond = allBondsForModal.value.find(b => b.code === bondCode);
        if (bond) {
          // 중복 허용을 위한 고유 key 추가
          const bondWithKey = {
            ...bond,
            _displayKey: `manual-${bondCode}-${index}`,
            _source: 'manual'
          };
          individualBonds.push(bondWithKey);
        }
      });

      // 일반 선택 채권들은 기본적으로 만기순 정렬 유지
      return sortBondsForDisplay(individualBonds);
    });

    // 채권들을 만기순으로 정렬하는 함수 (모든 채권 목록에 사용)
    const sortBondsForDisplay = bonds => {
      return [...bonds].sort((a, b) => {
        // 만기일 기준 오름차순 정렬 (가까운 만기가 위로)
        const aMaturityDate = new Date(a.maturityDate);
        const bMaturityDate = new Date(b.maturityDate);

        // 만기일이 없는 경우 처리
        if (!a.maturityDate && !b.maturityDate) return 0;
        if (!a.maturityDate) return 1; // 만기일이 없는 것은 맨 아래
        if (!b.maturityDate) return -1;

        return aMaturityDate - bMaturityDate;
      });
    };

    // 채권 타입 변경 함수 (국고채/통안채 전환)
    const switchBondType = type => {
      selectedBondType.value = type;
      // 타입 변경 시 선택된 채권 초기화
      selectedBond.value = null;
      selectedBondName.value = '';
    };

    // Fetch initial data
    onMounted(async () => {
      // 기본 데이터 로드
      await loadInitialData();
      loadSavedGroups(); // 호환성을 위해 유지

      // 선택 상태 복원 (바스켓 로드 포함)
      await restoreAllSelectionStates();

      // 모든 초기화가 완료된 후 SSE 연결 시작 (약간의 지연을 두어 중복 방지)
      setTimeout(() => {
        initializeSSEConnection();
      }, 100);

      // 페이지 가시성 변경 이벤트 리스너 등록
      document.addEventListener('visibilitychange', handleVisibilityChange);

      // 초기 시작
      if (!document.hidden) {
        startHealthCheck();
      }

      // 페이지 언로드 시 SSE 연결 정리
      const handleBeforeUnload = () => {
        disconnectSSE();
      };

      window.addEventListener('beforeunload', handleBeforeUnload);
      window.addEventListener('pagehide', handleBeforeUnload);

      // 컴포넌트 언마운트 시 이벤트 리스너 제거
      onUnmounted(() => {
        window.removeEventListener('beforeunload', handleBeforeUnload);
        window.removeEventListener('pagehide', handleBeforeUnload);
      });
    });

    // 컴포넌트 언마운트 시 SSE 연결 정리
    onUnmounted(() => {
      // 이벤트 리스너 제거 및 인터벌 정리
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (healthCheckInterval) {
        clearInterval(healthCheckInterval);
      }
      disconnectSSE();
    });

    // 라우트 변경 시 SSE 연결 정리
    onBeforeRouteLeave((to, from, next) => {
      disconnectSSE();
      next();
    });



    // 선택된 국고채/통안채 유형 변경 시 선택된 채권 유형도 자동 전환
    watch(
      selectedKtbTypes,
      newValue => {
        if (newValue && newValue.length > 0) {
          selectedBondType.value = 'KTB';
        }

        if (newValue && typeof newValue === 'object' && newValue.bonds) {
          // 선택된 채권들을 manuallySelectedBonds에 반영
          manuallySelectedBonds.value = [...newValue.bonds];
        } else if (!newValue || newValue.length === 0) {
          // 선택된 채권이 없는 경우 초기화
          manuallySelectedBonds.value = [];
        }

        // 로컬스토리지에 국고채 선택 상태 저장
        saveKtbSelectionState();
      },
      { deep: true }
    );

    // 수동 선택 채권 변경 시 로컬스토리지에 저장
    watch(
      manuallySelectedBonds,
      () => {
        saveManualBondsState();
      },
      { deep: true }
    );

    // MSB 관련 watch는 현재 사용하지 않으므로 주석 처리
    // watch(
    //   selectedMsbTypes,
    //   newValue => {
    //     if (newValue && newValue.length > 0) {
    //       selectedBondType.value = 'MSB';
    //     }
    //   },
    //   { deep: true }
    // );





    // KTB 유형 레이블 가져오기
    const getTypeLabel = type => {
      const labels = {
        Y01: '연내',
        Y02: '2년',
        Y03: '3년',
        Y05: '5년',
        Y10: '10년',
        Y20: '20년',
        Y30: '30년',
      };
      return labels[type] || type;
    };

    const fetchChats = async (date, code) => {
      try {
        // 새로운 KTB 채팅 API 사용
        const newChatList = await ktbService.findKtbChat(date, code);

        // 새 채팅 목록 설정
        chatList.value = newChatList;


      } catch (error) {
        console.error('Failed to fetch chats:', error);
      }
    };

    // 채권 선택 시 채팅 불러오기
    const selectBond = bondCode => {
      // 같은 채권을 다시 선택한 경우는 무시
      if (selectedBond.value && selectedBond.value.code === bondCode) return;

      // 선택된 채권 코드로 채권 정보 찾기
      let selectedBondInfo = null;

      // 1. 먼저 전체 채권 데이터(allBondsForModal)에서 찾기 - 바스켓과 개별선택 모두 포함
      selectedBondInfo = allBondsForModal.value.find(bond => bond.code === bondCode);

      // 2. 찾지 못한 경우 ktbBonds 원본 데이터에서 찾기
      if (!selectedBondInfo) {
        for (const typeId in ktbBonds.value) {
          const bonds = ktbBonds.value[typeId] || [];
          const foundBond = bonds.find(bond => bond.code === bondCode);
          if (foundBond) {
            // 견적 데이터와 결합
            const quote = ktbQuotes.value.get(foundBond.code);
            selectedBondInfo = {
              ...foundBond,
              bondType: 'KTB',
              ktbType: typeId,
              name: quote?.name || foundBond.alias,
              evaluatedYield: quote?.evaluatedYield || null,
              bidYield: quote?.bidYield || null,
              askYield: quote?.askYield || null,
              bidAt: quote?.bidAt || null,
              askAt: quote?.askAt || null,
              totalTradingVolume: quote?.totalTradingVolume || 0,
              maturityDate: foundBond.maturityDate,
              yield: quote?.evaluatedYield || 'N/A',
              buyPrice: quote?.bidYield?.yield || 'N/A',
              sellPrice: quote?.askYield?.yield || 'N/A',
              volume: quote?.totalTradingVolume ? (quote.totalTradingVolume / 100000000).toFixed(0) : '0',
            };
            break;
          }
        }
      }

      // 3. MSB 데이터에서도 찾기 (향후 확장성을 위해)
      if (!selectedBondInfo) {
        const foundMsbBond = msbBonds.value.find(bond => bond.code === bondCode);
        if (foundMsbBond) {
          selectedBondInfo = foundMsbBond;
        }
      }

      if (selectedBondInfo) {
        selectedBondName.value = selectedBondInfo.name || selectedBondInfo.alias;
        selectedBond.value = selectedBondInfo;

        // 채팅 데이터 로드 (초기 데이터)
        fetchChats(selectedDate.value, bondCode);

      } else {
        console.warn('선택된 채권 정보를 찾을 수 없습니다:', bondCode);
      }
    };



    // 통합 SSE 연결 함수
    const connectSSEStream = (date, username) => {
      // 이미 연결 중이거나 연결된 상태면 중복 연결 방지
      if (isSSEConnecting.value || sseManager.isConnected()) {
        return;
      }

      isSSEConnecting.value = true;

      const baseURL = process.env.VUE_APP_API_BASE_URL || 'http://localhost:8080';
      const url = `${baseURL}/api/v1/quote/ktb/stream?date=${date}&username=${username}`;

      // 이벤트 핸들러 정의
      const handlers = {
        'quote-updated': (event) => {
          try {
            const data = JSON.parse(event.data);
            handleQuoteUpdate(data);
          } catch (error) {
            console.error('❌ quote-updated 파싱 오류:', error);
          }
        },
        'chat-updated': (event) => {
          try {
            const data = JSON.parse(event.data);
            handleChatUpdate(data);
          } catch (error) {
            console.error('❌ chat-updated 파싱 오류:', error);
          }
        }
      };

      // SSE 연결 시작
      try {
        sseManager.connect(url, handlers);

        // 연결 상태 확인을 위한 타이머 설정
        const connectionCheckTimer = setTimeout(() => {
          if (sseManager.isConnected()) {
            isSSEConnecting.value = false;
          } else {
            isSSEConnecting.value = false;
          }
        }, 2000);

        // 연결 성공/실패 후 타이머 정리를 위한 참조 저장
        sseManager.connectionCheckTimer = connectionCheckTimer;

      } catch (error) {
        console.error('❌ SSE 연결 시도 실패:', error);
        isSSEConnecting.value = false;
      }
    };

    // quote-updated 이벤트 핸들러
    const handleQuoteUpdate = (quoteUpdateEvent) => {
      const { code, tradeType, hasQuoteYield, quoteYield, updatedAt } = quoteUpdateEvent;

      if (!hasQuoteYield || !quoteYield) {
        return;
      }

      // 기존 견적 데이터 가져오기
      const existingData = ktbQuotes.value.get(code);
      if (!existingData) {
        return;
      }

      // 새로운 yield 객체 생성
      const newYieldData = {
        yield: quoteYield.yield,
        setYield: quoteYield.setYield,
        isSet: quoteYield.isSet
      };

      // 기존 데이터 업데이트 (bidAt, askAt 포함)
      const updatedData = {
        ...existingData,
        bidYield: tradeType === 'BUY' ? newYieldData : existingData.bidYield,
        askYield: tradeType === 'SELL' ? newYieldData : existingData.askYield,
        bidAt: tradeType === 'BUY' ? updatedAt : existingData.bidAt,
        askAt: tradeType === 'SELL' ? updatedAt : existingData.askAt,
        lastUpdate: new Date().toISOString()
      };

      // ktbQuotes 업데이트
      const newMap = new Map(ktbQuotes.value);
      newMap.set(code, updatedData);
      ktbQuotes.value = newMap;

      // allBonds 업데이트
      const bondIndex = allBonds.value.findIndex(bond => bond.code === code);
      if (bondIndex !== -1) {
        const updatedBond = {
          ...allBonds.value[bondIndex],
          bidYield: tradeType === 'BUY' ? newYieldData : allBonds.value[bondIndex].bidYield,
          askYield: tradeType === 'SELL' ? newYieldData : allBonds.value[bondIndex].askYield,
          bidAt: tradeType === 'BUY' ? updatedAt : allBonds.value[bondIndex].bidAt,
          askAt: tradeType === 'SELL' ? updatedAt : allBonds.value[bondIndex].askAt,
          lastUpdate: new Date().toISOString()
        };

        // Vue의 반응성을 보장하기 위해 새 배열 생성
        allBonds.value = [
          ...allBonds.value.slice(0, bondIndex),
          updatedBond,
          ...allBonds.value.slice(bondIndex + 1)
        ];

      }

      // 애니메이션을 위한 업데이트된 채권 코드 설정 (타임스탬프 포함)
      lastUpdatedBondCode.value = {
        code: code,
        timestamp: Date.now()
      };
    };

    // chat-updated 이벤트 핸들러
    const handleChatUpdate = (chatView) => {
      // 현재 선택된 채권과 일치하는 경우에만 처리
      if (selectedBond.value && selectedBond.value.code === chatView.code) {
        // 채팅 목록 최하단에 추가
        const updatedChats = [...chatList.value, chatView];
        // 시간순 정렬 (최신이 아래로)
        updatedChats.sort((a, b) => new Date(a.chatDateTime) - new Date(b.chatDateTime));
        chatList.value = updatedChats;

        // 호가 정보가 있는 채팅인 경우 새 호가 알림 활성화 및 채권 정보 업데이트
        if (chatView.quoteYield?.yield !== null && chatView.tradeType) {
          hasNewQuote.value = true;

          // 선택된 채권의 호가 정보 업데이트
          const updatedBond = { ...selectedBond.value };

          if (chatView.tradeType === 'SELL') {
            // 매도 호가 업데이트 (askYield)
            updatedBond.askYield = chatView.quoteYield;
            updatedBond.sellPrice = chatView.quoteYield.yield || 'N/A';
            updatedBond.askAt = chatView.chatDateTime || new Date().toISOString(); // 시간 정보 업데이트
            updatedBond.lastAskUpdate = Date.now(); // 애니메이션용 타임스탬프
          } else if (chatView.tradeType === 'BUY') {
            // 매수 호가 업데이트 (bidYield)
            updatedBond.bidYield = chatView.quoteYield;
            updatedBond.buyPrice = chatView.quoteYield.yield || 'N/A';
            updatedBond.bidAt = chatView.chatDateTime || new Date().toISOString(); // 시간 정보 업데이트
            updatedBond.lastBidUpdate = Date.now(); // 애니메이션용 타임스탬프
          }

          selectedBond.value = updatedBond;
        }

      }
    };

    // 새 호가 알림 제거
    const clearNewQuoteAlert = () => {
      hasNewQuote.value = false;
    };



    // SSE 연결 해제
    const disconnectSSE = () => {
      isSSEConnecting.value = false;
      sseManager.disconnect();
    };

    const formatTime = datetime => {
      return format(new Date(datetime), 'HH:mm:ss');
    };

    // 그룹 관련 함수
    const loadSavedGroups = () => {
      try {
        const storedGroups = localStorage.getItem(BOND_GROUPS_STORAGE_KEY);
        if (storedGroups) {
          savedGroups.value = JSON.parse(storedGroups);
        } else {
          // 저장된 데이터가 없을 경우 빈 배열로 초기화
          savedGroups.value = [];
        }
      } catch (error) {
        console.error('Failed to load bond groups from localStorage:', error);
        // 에러 발생 시 빈 배열로 초기화
        savedGroups.value = [];
      }
    };

    // 그룹 저장 함수
    const saveSavedGroups = () => {
      try {
        localStorage.setItem(
          BOND_GROUPS_STORAGE_KEY,
          JSON.stringify(savedGroups.value)
        );
      } catch (error) {
        console.error('Failed to save bond groups to localStorage:', error);
      }
    };

    // === 로컬스토리지 관리 함수들 ===

    // 바스켓 선택 상태 저장
    const saveBasketSelectionState = () => {
      try {
        const selectionState = {
          selectedBasketIds: selectedBasketIds.value,
          timestamp: Date.now()
        };
        localStorage.setItem(BASKET_SELECTION_STORAGE_KEY, JSON.stringify(selectionState));
      } catch (error) {
        console.error('바스켓 선택 상태 저장 실패:', error);
      }
    };

    // 국고채 선택 상태 저장
    const saveKtbSelectionState = () => {
      try {
        const selectionState = {
          selectedKtbTypes: selectedKtbTypes.value,
          timestamp: Date.now()
        };
        localStorage.setItem(KTB_SELECTION_STORAGE_KEY, JSON.stringify(selectionState));
      } catch (error) {
        console.error('국고채 선택 상태 저장 실패:', error);
      }
    };

    // 수동 선택 채권 상태 저장
    const saveManualBondsState = () => {
      try {
        const selectionState = {
          manuallySelectedBonds: manuallySelectedBonds.value,
          timestamp: Date.now()
        };
        localStorage.setItem(MANUAL_BONDS_STORAGE_KEY, JSON.stringify(selectionState));
      } catch (error) {
        console.error('수동 선택 채권 상태 저장 실패:', error);
      }
    };

    // 바스켓 선택 상태 복원
    const restoreBasketSelectionState = () => {
      try {
        const stored = localStorage.getItem(BASKET_SELECTION_STORAGE_KEY);
        if (stored) {
          const selectionState = JSON.parse(stored);
          // 24시간 이내의 데이터만 복원 (86400000ms = 24시간)
          if (Date.now() - selectionState.timestamp < 86400000) {
            selectedBasketIds.value = selectionState.selectedBasketIds || [];
            return true;
          }
        }
      } catch (error) {
        console.error('바스켓 선택 상태 복원 실패:', error);
      }
      return false;
    };

    // 국고채 선택 상태 복원
    const restoreKtbSelectionState = () => {
      try {
        const stored = localStorage.getItem(KTB_SELECTION_STORAGE_KEY);
        if (stored) {
          const selectionState = JSON.parse(stored);
          // 24시간 이내의 데이터만 복원
          if (Date.now() - selectionState.timestamp < 86400000) {
            selectedKtbTypes.value = selectionState.selectedKtbTypes || [];
            return true;
          }
        }
      } catch (error) {
        console.error('국고채 선택 상태 복원 실패:', error);
      }
      return false;
    };

    // 수동 선택 채권 상태 복원
    const restoreManualBondsState = () => {
      try {
        const stored = localStorage.getItem(MANUAL_BONDS_STORAGE_KEY);
        if (stored) {
          const selectionState = JSON.parse(stored);
          // 24시간 이내의 데이터만 복원
          if (Date.now() - selectionState.timestamp < 86400000) {
            manuallySelectedBonds.value = selectionState.manuallySelectedBonds || [];
            return true;
          }
        }
      } catch (error) {
        console.error('수동 선택 채권 상태 복원 실패:', error);
      }
      return false;
    };

    // 모든 선택 상태 저장
    const saveAllSelectionStates = () => {
      saveBasketSelectionState();
      saveKtbSelectionState();
      saveManualBondsState();
    };

    // 모든 선택 상태 복원
    const restoreAllSelectionStates = async () => {

      // 바스켓 목록이 로드될 때까지 대기
      await loadUserBaskets();

      // 선택 상태들 복원
      const basketRestored = restoreBasketSelectionState();
      const ktbRestored = restoreKtbSelectionState();
      const manualRestored = restoreManualBondsState();

      if (basketRestored || ktbRestored || manualRestored) {
        return true;
      }

      return false;
    };

    // 모달 관련 상태
    const showSaveModal = ref(false);
    const newGroupName = ref('');
    const isEditMode = ref(false);
    const editingBasketId = ref(null);

    // 모달 내 상태
    const modalSelectedBonds = ref([]);
    const bondSearchQuery = ref('');
    const draggedBond = ref(null);

    // 모달 열기 (기존 호환성을 위해 유지)
    const openSaveModal = () => {
      openCreateBasketModal();
    };

    // 채권이 수동 선택되었는지 확인
    const isSelectedForGroup = code => {
      return manuallySelectedBonds.value.includes(code);
    };

    // 채권 코드로 이름 조회
    const getBondNameByCode = code => {
      for (const typeId in ktbBonds.value) {
        const bond = ktbBonds.value[typeId]?.find(bond => bond.code === code);
        if (bond) {
          return bond.bondName; // 전체 bondName 반환
        }
      }
      return code; // 이름을 찾지 못한 경우 코드 반환
    };

    // 채권 코드로 타입 조회
    const getBondTypeByCode = code => {
      for (const typeId in ktbBonds.value) {
        const bond = ktbBonds.value[typeId]?.find(bond => bond.code === code);
        if (bond) {
          return getTypeLabel(typeId);
        }
      }
      return '-';
    };

    // 채권 코드로 수익률 조회
    const getBondYieldByCode = code => {
      for (const typeId in ktbBonds.value) {
        const bond = ktbBonds.value[typeId]?.find(bond => bond.code === code);
        if (bond && bond.yield) {
          // 문자열이나 숫자 모두 처리할 수 있도록 parseFloat 사용
          return parseFloat(bond.yield).toFixed(3) + '%';
        }
      }
      return null;
    };

    // 채권을 수동 선택 목록에 추가/제거
    const toggleSelectedBond = bond => {
      const index = manuallySelectedBonds.value.findIndex(c => c === bond.code);
      if (index === -1) {
        // 선택되지 않은 경우 추가
        manuallySelectedBonds.value.push(bond.code);
      } else {
        // 이미 선택된 경우 제거
        manuallySelectedBonds.value.splice(index, 1);
      }

      // KtbTypeComponent의 선택 상태도 업데이트
      if (
        selectedKtbTypes.value &&
        typeof selectedKtbTypes.value === 'object' &&
        selectedKtbTypes.value.bonds
      ) {
        const bondIndex = selectedKtbTypes.value.bonds.indexOf(bond.code);
        if (bondIndex !== -1) {
          selectedKtbTypes.value.bonds.splice(bondIndex, 1);
        }
      }
    };

    // 채권 코드로 매수호가 조회
    const getBondBuyPriceByCode = code => {
      for (const typeId in ktbBonds.value) {
        const bond = ktbBonds.value[typeId]?.find(bond => bond.code === code);
        if (bond && bond.buyPrice) {
          // 문자열이나 숫자 모두 처리할 수 있도록 parseFloat 사용
          return parseFloat(bond.buyPrice).toFixed(3);
        }
      }
      return null;
    };

    // 채권 코드로 매도호가 조회
    const getBondSellPriceByCode = code => {
      for (const typeId in ktbBonds.value) {
        const bond = ktbBonds.value[typeId]?.find(bond => bond.code === code);
        if (bond && bond.sellPrice) {
          // 문자열이나 숫자 모두 처리할 수 있도록 parseFloat 사용
          return parseFloat(bond.sellPrice).toFixed(3);
        }
      }
      return null;
    };

    // 수동 선택 목록에서 채권 제거
    const removeBondFromSelection = code => {
      const index = manuallySelectedBonds.value.findIndex(c => c === code);
      if (index !== -1) {
        manuallySelectedBonds.value.splice(index, 1);
      }

      // KtbTypeComponent의 선택 상태도 업데이트
      if (
        selectedKtbTypes.value &&
        typeof selectedKtbTypes.value === 'object' &&
        selectedKtbTypes.value.bonds
      ) {
        const bondIndex = selectedKtbTypes.value.bonds.indexOf(code);
        if (bondIndex !== -1) {
          selectedKtbTypes.value.bonds.splice(bondIndex, 1);
        }
      }
    };

    // 선택된 채권 모두 제거
    const clearSelectedBonds = () => {
      manuallySelectedBonds.value = [];
    };

    // 바스켓 저장 (생성 또는 수정) - 수정된 버전
    const saveGroup = async () => {
      const basketName = newGroupName.value.trim();
      if (!basketName) {
        alert('바스켓 이름을 입력해주세요.');
        return;
      }

      const bondCodes = [...modalSelectedBonds.value];
      if (bondCodes.length === 0) {
        alert('최소 1개 이상의 종목을 선택해주세요.');
        return;
      }

      try {
        if (isEditMode.value && editingBasketId.value) {
          // 바스켓 수정 (이름만 업데이트, 아이템은 드래그앤드롭 시 실시간 처리됨)
          await basketService.updateBasketName(editingBasketId.value, basketName);
        } else {
          // 새 바스켓 생성
          await basketService.createBasket(basketName, bondCodes);
        }

        // 바스켓 목록 새로고침
        await loadUserBaskets();

        // 편집 모드일 때 모달의 아이템 목록도 업데이트
        if (isEditMode.value && editingBasketId.value) {
          const updatedBasket = userBaskets.value.find(b => b.id === editingBasketId.value);
          if (updatedBasket && updatedBasket.items) {
            modalSelectedBonds.value = updatedBasket.items.map(item => item.bondCode);
          }
        }

        // 성공 메시지
        const message = isEditMode.value ? '바스켓이 수정되었습니다.' : '바스켓이 생성되었습니다.';

        // 모달 닫기
        closeSaveModal();

        // 성공 알림은 모달이 닫힌 후에 표시
        setTimeout(() => {
          alert(message);
        }, 100);

      } catch (error) {
        console.error('바스켓 저장 실패:', error);

        // 오류 상세 정보 확인
        if (error.response) {
          const errorMessage = error.response.data?.message || '바스켓 저장에 실패했습니다.';
          alert(errorMessage);
        } else {
          alert('바스켓 저장에 실패했습니다.');
        }
      }
    };

    // 그룹 삭제
    const removeGroup = index => {
      if (confirm('정말 이 그룹을 삭제하시겠습니까?')) {
        // 현재 활성화된 그룹이 삭제되는 경우 활성화 상태 초기화
        if (activeGroupIndex.value === index) {
          activeGroupIndex.value = -1;
        } else if (activeGroupIndex.value > index) {
          // 삭제되는 그룹보다 뒤에 있는 그룹이 활성화된 경우 인덱스 조정
          activeGroupIndex.value--;
        }

        savedGroups.value.splice(index, 1);
        saveSavedGroups();
      }
    };

    // 그룹화된 채권 타입 목록 (2년, 3년, 5년 등)
    const groupedBondTypes = computed(() => {
      const types = new Set();
      selectedBondsForGroup.value.forEach(code => {
        const type = getBondTypeByCode(code);
        if (type) {
          types.add(type);
        }
      });
      return Array.from(types).sort();
    });

    // 특정 타입에 속하는 채권 코드 목록 반환
    const getBondsByType = type => {
      return selectedBondsForGroup.value.filter(
        code => getBondTypeByCode(code) === type
      );
    };

    // 모든 채권 선택 (수동 선택 목록에 추가)
    const selectAllBonds = () => {
      // 모든 유형의 채권을 수동 선택
      for (const type in ktbBonds.value) {
        if (ktbBonds.value[type] && ktbBonds.value[type].length > 0) {
          ktbBonds.value[type].forEach(bond => {
            if (!isSelectedForGroup(bond.code)) {
              manuallySelectedBonds.value.push(bond.code);
            }
          });
        }
      }
    };

    // === 바스켓 관리 함수들 ===

    // 사용자 바스켓 목록 로드
    const loadUserBaskets = async () => {
      try {
        const baskets = await basketService.getBasketsWithItems();
        userBaskets.value = baskets || [];
      } catch (error) {
        console.error('바스켓 목록 로드 실패:', error);
        userBaskets.value = [];
      }
    };


    // 바스켓 선택 함수 (복수 선택 지원)
    const selectBasket = (basketId) => {
      const basketIdStr = basketId.toString();
      const currentIndex = selectedBasketIds.value.indexOf(basketIdStr);

      if (currentIndex >= 0) {
        // 이미 선택된 바스켓을 다시 클릭하면 선택 해제
        selectedBasketIds.value.splice(currentIndex, 1);
      } else {
        // 새로운 바스켓 선택
        selectedBasketIds.value.push(basketIdStr);
      }

      // 선택된 모든 바스켓의 채권 타입에 따라 KTB/MSB 타입 자동 설정

      // 로컬스토리지에 바스켓 선택 상태 저장
      saveBasketSelectionState();

    };

    // 바스켓 아이템 개수 조회
    const getBasketItemCount = (basketId) => {
      const basket = userBaskets.value.find(b => b.id === basketId);
      return basket?.items?.length || 0;
    };

    // 바스켓 생성 모달 열기 (수정됨 - 선택된 종목 없이도 가능)
    const openCreateBasketModal = () => {
      if (userBaskets.value.length >= 5) {
        alert('최대 5개의 바스켓만 만들 수 있습니다.');
        return;
      }

      isEditMode.value = false;
      editingBasketId.value = null;
      showSaveModal.value = true;
      newGroupName.value = '';
      modalSelectedBonds.value = [];
      bondSearchQuery.value = '';
    };

    // 바스켓 수정 함수 (새로운 UI용)
    const editBasket = (basket) => {
      isEditMode.value = true;
      editingBasketId.value = basket.id;
      showSaveModal.value = true;
      newGroupName.value = basket.name;
      modalSelectedBonds.value = basket.items ? basket.items.map(item => item.bondCode) : [];
      bondSearchQuery.value = '';
    };

    // 바스켓 삭제 함수 (복수 선택 지원)
    const deleteBasket = async (basket) => {
      if (confirm(`'${basket.name}' 바스켓을 삭제하시겠습니까?`)) {
        try {
          await basketService.deleteBasket(basket.id);

          // 선택된 바스켓 목록에서 삭제된 바스켓 제거
          const basketIdStr = basket.id.toString();
          const index = selectedBasketIds.value.indexOf(basketIdStr);
          if (index >= 0) {
            selectedBasketIds.value.splice(index, 1);

            // 로컬스토리지에 업데이트된 바스켓 선택 상태 저장
            saveBasketSelectionState();
          }

          await loadUserBaskets();
          alert('바스켓이 삭제되었습니다.');
        } catch (error) {
          console.error('바스켓 삭제 실패:', error);
          alert('바스켓 삭제에 실패했습니다.');
        }
      }
    };

    // 기존 함수들 제거됨 - 복수 바스켓 선택으로 대체

    // 복수 바스켓 선택 관련 함수들
    const getSelectedBasketsNames = () => {
      return selectedBasketIds.value.map(basketIdStr => {
        const basket = userBaskets.value.find(b => b.id === parseInt(basketIdStr));
        return basket ? basket.name : '';
      }).filter(name => name);
    };

    const clearAllBasketSelections = () => {
      selectedBasketIds.value = [];
      manuallySelectedBonds.value = [];
      selectedKtbTypes.value = [];

      // 로컬스토리지 상태도 모두 초기화
      saveAllSelectionStates();
    };

    // === 모달 관련 함수들 ===

    // 모든 채권 목록 (모달에서 사용) - 필터링되지 않은 전체 데이터
    const allBondsForModal = computed(() => {
      const result = [];

      // 모든 KTB 채권 추가 (타입 필터링 없이)
      for (const typeId in ktbBonds.value) {
        const bonds = ktbBonds.value[typeId] || [];
        bonds.forEach(bond => {
          const quote = ktbQuotes.value.get(bond.code);

          result.push({
            ...bond,
            bondType: 'KTB',
            ktbType: typeId,
            name: quote?.name || bond.alias,
            evaluatedYield: quote?.evaluatedYield || null,
            bidYield: quote?.bidYield || null,
            askYield: quote?.askYield || null,
            bidAt: quote?.bidAt || null,
            askAt: quote?.askAt || null,
            totalTradingVolume: quote?.totalTradingVolume || 0,
            maturityDate: bond.maturityDate,
            yield: quote?.evaluatedYield || 'N/A',
            buyPrice: quote?.bidYield?.yield || 'N/A',
            sellPrice: quote?.askYield?.yield || 'N/A',
            volume: quote?.totalTradingVolume ? (quote.totalTradingVolume / 100000000).toFixed(0) : '0',
          });
        });
      }

      // MSB 채권도 추가 (현재는 빈 배열이지만 확장성을 위해)
      result.push(...processedMsbBonds.value);

      return result;
    });

    // 검색 필터가 적용된 전체 채권 목록 (모달에서 사용)
    const filteredAllBonds = computed(() => {
      let bonds = allBondsForModal.value;

      // 검색 필터 적용
      if (bondSearchQuery.value.trim()) {
        const query = bondSearchQuery.value.toLowerCase().trim();
        bonds = bonds.filter(bond => {
          const bondName = (bond.alias || bond.bondName || '').toLowerCase();
          const formattedName = formatBondName(bondName).toLowerCase();
          return bondName.includes(query) || formattedName.includes(query) || bond.code.toLowerCase().includes(query);
        });
      }

      // 잔존만기 순으로 정렬 (만기일 기준 오름차순)
      bonds = bonds.sort((a, b) => {
        if (!a.maturityDate && !b.maturityDate) return 0;
        if (!a.maturityDate) return 1;
        if (!b.maturityDate) return -1;
        return new Date(a.maturityDate) - new Date(b.maturityDate);
      });

      return bonds;
    });

    // 모달에서 채권 추가
    const addBondToModal = async (bondCode) => {
      if (!modalSelectedBonds.value.includes(bondCode)) {
        modalSelectedBonds.value.push(bondCode);

        // 편집 모드일 때 즉시 API 호출
        if (isEditMode.value && editingBasketId.value) {
          try {
            await basketService.addBasketItem(editingBasketId.value, bondCode);
          } catch (error) {
            console.error('바스켓 아이템 추가 실패:', error);
            // 실패 시 모달에서도 제거
            modalSelectedBonds.value.pop();
            alert('바스켓에 종목을 추가하는데 실패했습니다.');
          }
        }
      }
    };

    // 모달에서 채권 제거
    const removeBondFromModal = async (bondCode) => {
      const index = modalSelectedBonds.value.indexOf(bondCode);
      if (index > -1) {
        // 편집 모드일 때 즈시 API 호출
        if (isEditMode.value && editingBasketId.value) {
          try {
            // 바스켓에서 해당 bondCode의 item ID를 찾아야 함
            const basket = userBaskets.value.find(b => b.id === editingBasketId.value);
            if (basket && basket.items) {
              const item = basket.items.find(item => item.bondCode === bondCode);
              if (item && item.id) {
                await basketService.removeBasketItem(editingBasketId.value, item.id);
                modalSelectedBonds.value.splice(index, 1);
              } else {
                console.warn('바스켓 아이템 ID를 찾을 수 없습니다:', bondCode);
                modalSelectedBonds.value.splice(index, 1); // 모달에서는 제거
              }
            }
          } catch (error) {
            console.error('바스켓 아이템 제거 실패:', error);
            alert('바스켓에서 종목을 제거하는데 실패했습니다.');
          }
        } else {
          // 생성 모드일 때는 모달에서만 제거
          modalSelectedBonds.value.splice(index, 1);
        }
      }
    };

    // 드래그 시작
    const handleDragStart = (bond) => {
      draggedBond.value = bond;
    };

    // 드래그 종료
    const handleDragEnd = () => {
      draggedBond.value = null;
    };

    // 드롭 처리
    const handleDrop = async (event) => {
      event.preventDefault();
      if (draggedBond.value && !modalSelectedBonds.value.includes(draggedBond.value.code)) {
        const bondCode = draggedBond.value.code;
        modalSelectedBonds.value.push(bondCode);

        // 편집 모드일 때 즉시 API 호출
        if (isEditMode.value && editingBasketId.value) {
          try {
            await basketService.addBasketItem(editingBasketId.value, bondCode);
          } catch (error) {
            console.error('드래그앤드롭 바스켓 아이템 추가 실패:', error);
            // 실패 시 모달에서 제거
            const index = modalSelectedBonds.value.indexOf(bondCode);
            if (index > -1) {
              modalSelectedBonds.value.splice(index, 1);
            }
            alert('바스켓에 종목을 추가하는데 실패했습니다.');
          }
        }
      }
      draggedBond.value = null;
    };

    // 모달 닫기 시 초기화
    const closeSaveModal = () => {
      showSaveModal.value = false;
      isEditMode.value = false;
      editingBasketId.value = null;
      newGroupName.value = '';
      modalSelectedBonds.value = [];
      bondSearchQuery.value = '';
      draggedBond.value = null;
    };

    // 클립보드 복사 함수 수정
    const copyToClipboard = text => {
      if (navigator.clipboard) {
        navigator.clipboard
          .writeText(text)
          .then(() => {
            // 성공적으로 복사되었을 때 사용자에게 알림
            showCopyNotification.value = true;
            setTimeout(() => {
              showCopyNotification.value = false;
            }, 2000); // 2초 후 알림 숨김
          })
          .catch(err => {
            console.error('클립보드 복사 실패:', err);
          });
      } else {
        // 폴백 방법: 일시적인 textarea 사용
        const textarea = document.createElement('textarea');
        textarea.value = text;
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);

        // 성공적으로 복사되었을 때 사용자에게 알림
        showCopyNotification.value = true;
        setTimeout(() => {
          showCopyNotification.value = false;
        }, 2000); // 2초 후 알림 숨김
      }
    };

    // 코드로 채권 검색 함수 (전체 채권 데이터에서 검색)
    const searchBondByCode = () => {
      // 입력값이 없는 경우 처리하지 않음
      if (!searchBondInput.value.trim()) return;

      const input = searchBondInput.value.trim();

      // 입력된 패턴이 4자리 숫자인지 확인 (예: 2407)
      const pattern = /^(\d{2})(\d{2})$/;
      const match = input.match(pattern);

      if (match) {
        // 4자리 숫자를 XX-XX 형식으로 변환 (예: 2407 -> 24-7 또는 24-07)
        const year = match[1];
        const issue = match[2];

        // 두 가지 형식으로 검색: XX-X (앞의 0 제거) 및 XX-XX (원본)
        const formattedCode1 = `${year}-${issue.replace(/^0+/, '')}`; // 24-7
        const formattedCode2 = `${year}-${issue}`; // 24-07

        let foundBond = null;

        // 전체 KTB 채권 데이터에서 검색 (ktbBonds에서 직접 검색)
        for (const typeId in ktbBonds.value) {
          const bonds = ktbBonds.value[typeId] || [];
          foundBond = bonds.find(bond => {
            // alias와 매칭 (한자리 숫자는 0 패딩 고려)
            const alias = bond.alias;
            return alias === formattedCode1 || alias === formattedCode2;
          });

          if (foundBond) break;
        }

        // MSB 채권 데이터에서도 검색 (향후 확장성을 위해)
        if (!foundBond) {
          foundBond = msbBonds.value.find(bond => {
            const alias = bond.alias;
            return alias === formattedCode1 || alias === formattedCode2;
          });
        }

        if (foundBond) {
          // 채권 선택 - 견적 데이터와 결합된 정보 전달
          const quote = ktbQuotes.value.get(foundBond.code);
          const bondWithQuote = {
            ...foundBond,
            name: quote?.name || foundBond.alias,
            evaluatedYield: quote?.evaluatedYield || null,
            bidYield: quote?.bidYield || null,
            askYield: quote?.askYield || null,
            bidAt: quote?.bidAt || null,
            askAt: quote?.askAt || null,
            totalTradingVolume: quote?.totalTradingVolume || 0,
            yield: quote?.evaluatedYield || 'N/A',
            buyPrice: quote?.bidYield?.yield || 'N/A',
            sellPrice: quote?.askYield?.yield || 'N/A',
            volume: quote?.totalTradingVolume ? (quote.totalTradingVolume / 100000000).toFixed(0) : '0',
          };

          // 선택된 채권 정보 업데이트
          selectedBond.value = bondWithQuote;
          selectedBondName.value = bondWithQuote.name || bondWithQuote.alias;

          // 채팅 데이터 로드
          fetchChats(selectedDate.value, foundBond.code);

          // 입력 필드 초기화
          searchBondInput.value = '';
        } else {
          // 일치하는 채권이 없는 경우 알림
          alert(`'${formattedCode1}' 또는 '${formattedCode2}' 코드와 일치하는 채권을 찾을 수 없습니다.`);
        }
      } else {
        // 입력 형식이 잘못된 경우 알림
        alert('4자리 숫자 형식으로 입력해주세요. (예: 2407)');
      }
    };

    // 복사 알림 상태 추가
    const showCopyNotification = ref(false);

    // 스프레드 계산 함수
    const calculateSpread = (baseYield, price) => {
      if (!baseYield || !price) return null;

      // 민평 수익률
      const baseYieldValue = parseFloat(baseYield);
      if (isNaN(baseYieldValue)) return null;

      // 가격에서 수익률 추출
      let priceValue = parseFloat(price);
      if (isNaN(priceValue)) return null;

      // 가격이 1000 미만인 경우 (수익률의 소수점 뒷부분)
      if (priceValue >= 0 && priceValue < 1000) {
        // 2자리 숫자(10~99)는 .xx0으로 해석 (예: 39 -> .390)
        // 3자리 숫자(100~999)는 .xxx로 해석 (예: 395 -> .395)
        if (priceValue < 100) {
          // 2자리 숫자: .xx0
          priceValue = Math.floor(baseYieldValue) + priceValue / 100 / 10;
        } else {
          // 3자리 숫자: .xxx
          priceValue = Math.floor(baseYieldValue) + priceValue / 1000;
        }
      }

      // 스프레드 계산 (베이시스 포인트, 1bp = 0.01%)
      // 민평 - 가격수익률 = 스프레드 (양수면 언더, 음수면 오버)
      return (baseYieldValue - priceValue) * 100;
    };

    // 스프레드 포맷팅 함수
    const formatSpread = spread => {
      if (spread === null || spread === undefined) return 'N/A';

      // 양수는 언더(+), 음수는 오버(-)로 표시
      const sign = spread >= 0 ? '+' : '';
      return `${sign}${spread.toFixed(1)}bp`;
    };

    // evaluatedYield 대비 bid/ask yield 스프레드 계산
    const calculateYieldSpread = (evaluatedYield, targetYield) => {
      if (!evaluatedYield || !targetYield) return null;

      // 베이시스 포인트로 계산 (1bp = 0.01%)
      return (targetYield - evaluatedYield) * 100;
    };

    // 거래량을 억 단위로 포맷팅
    const formatTradingVolume = (volume) => {
      if (!volume || volume === 0) return '0';
      return (volume / 100000000).toFixed(0);
    };



    // 정렬 토글 함수
    const toggleSort = field => {
      if (sortBy.value === field) {
        // 같은 필드를 다시 클릭하면 정렬 방향 토글
        sortDirection.value = sortDirection.value === 'asc' ? 'desc' : 'asc';
      } else {
        // 다른 필드를 클릭하면 해당 필드로 정렬 변경
        sortBy.value = field;
        sortDirection.value = 'asc';
      }
    };

    // Watch for date changes - 날짜가 변경될 때 견적 데이터 다시 로드 및 채팅 갱신
    watch(selectedDate, async (newDate) => {
      // 견적 데이터 다시 로드
      loadKtbQuotes();

      // 기존 SSE 연결 종료
      disconnectSSE();

      // SSE 연결 재시작 (약간의 지연을 두어 완전한 종료 후 재연결)
      setTimeout(() => {
        const username = localStorage.getItem('username');
        if (username) {
          connectSSEStream(newDate, username);
        }
      }, 300);

      if (selectedBondName.value) {
        // 선택한 채권의 실제 코드 찾기
        let actualBondCode = null;
        for (const typeId in ktbBonds.value) {
          const foundBond = ktbBonds.value[typeId]?.find(
            bond => formatBondName(bond.bondName) === selectedBondName.value
          );
          if (foundBond) {
            actualBondCode = foundBond.code;
            break;
          }
        }

        if (actualBondCode) {
          fetchChats(newDate, actualBondCode);
        }
      }
    });

    // // Watch for chat list changes - 새로운 채팅이 추가될 때 스크롤을 아래로 이동
    // watch(chatList, () => {
    //   scrollToBottom();
    // });

    // 잔존만기 포맷팅 함수
    const formatRemainingTerm = maturityDate => {
      if (!maturityDate) return 'N/A';

      try {
        const today = new Date();
        const maturity = new Date(maturityDate);

        // 전체 개월 수 계산
        const monthsDiff = differenceInMonths(maturity, today);

        if (monthsDiff < 12) {
          // 1년 미만: x개월
          return `${monthsDiff}개월`;
        } else {
          // 1년 이상: x년 y개월
          const years = Math.floor(monthsDiff / 12);
          const months = monthsDiff % 12;

          if (months === 0) {
            return `${years}년`;
          } else {
            return `${years}년 ${months}개월`;
          }
        }
      } catch (error) {
        console.error('만기일 포맷 오류:', error);
        return 'N/A';
      }
    };

    const formatBondName = bondName => {
      if (!bondName) return '';

      // 괄호 안의 내용 추출 (예: 국고04250-2512(22-13) -> 22-13)
      const match = bondName.match(/\(([^)]+)\)$/);
      if (match && match[1]) {
        return match[1];
      }

      // 괄호가 없는 경우 원래 문자열 반환
      return bondName;
    };

    // 필터링된 채팅 목록 (서버에서 분석된 데이터 사용)
    const filteredChatList = computed(() => {
      let filtered = chatList.value;

      // 자투리 제외 필터 (서버 데이터의 isFractional 사용)
      if (hideFragmentTrades.value) {
        filtered = filtered.filter(chat => !chat.isFractional);
      }

      // 가격 미포함 제외 필터 (서버 데이터의 quoteYield 사용)
      if (hideWithoutPrice.value) {
        filtered = filtered.filter(chat => chat.quoteYield && chat.quoteYield.yield);
      }

      return filtered;
    });

    // BondListComponent에 전달할 선택된 바스켓 데이터
    const selectedBasketsData = computed(() => {
      return selectedBasketIds.value.map(basketIdStr => {
        const basketId = parseInt(basketIdStr);
        return userBaskets.value.find(b => b.id === basketId);
      }).filter(Boolean); // null/undefined 제거
    });

    // 선택된 복수 바스켓들을 그룹들로 변환 (레거시 지원)
    const basketAsGroups = computed(() => {
      if (selectedBasketIds.value.length === 0) {
        return [];
      }

      const basketGroups = [];

      selectedBasketIds.value.forEach(basketIdStr => {
        const basketId = parseInt(basketIdStr);
        const basket = userBaskets.value.find(b => b.id === basketId);

        if (basket && basket.items) {
          // 바스켓의 채권들을 찾아서 그룹 형태로 변환
          const basketBonds = [];
          basket.items.forEach((item, itemIndex) => {
            if (item.bondCode) {
              // 바스켓에서는 필터링되지 않은 전체 채권 데이터 사용
              const bond = allBondsForModal.value.find(b => b.code === item.bondCode);
              if (bond) {
                // 중복 허용을 위한 고유 key 추가
                const bondWithKey = {
                  ...bond,
                  _displayKey: `basket-${basket.id}-${item.bondCode}-${itemIndex}`,
                  _source: 'basket',
                  _basketId: basket.id
                };
                basketBonds.push(bondWithKey);
              }
            }
          });

          if (basketBonds.length > 0) {
            basketGroups.push({
              id: `basket-${basket.id}`,
              label: `📋 ${basket.name}`,
              bonds: sortBondsForDisplay(basketBonds), // 바스켓 내 채권들도 만기순 정렬
              isBasket: true
            });
          }
        }
      });

      return basketGroups;
    });

    // 기존 그룹화 + 바스켓 그룹들을 결합
    const combinedGroupedBonds = computed(() => {
      const groups = [];

      // 바스켓 그룹들을 최상단에 추가
      groups.push(...basketAsGroups.value);

      // 기존 그룹화 결과 추가
      if (isGroupedView.value) {
        groups.push(...groupedBondsByType.value);
      }

      return groups;
    });

    // 채권을 유형별로 그룹화하는 계산 속성
    const groupedBondsByType = computed(() => {
      // 그룹화를 위한 맵 생성
      const groupMap = new Map();

      // 수동 선택된 채권만 필터링 (바스켓 제외)
      const manualBonds = [];
      manuallySelectedBonds.value.forEach((bondCode, index) => {
        // 그룹화에서도 전체 데이터에서 찾기
        const bond = allBondsForModal.value.find(b => b.code === bondCode);
        if (bond) {
          const bondWithKey = {
            ...bond,
            _displayKey: `manual-group-${bondCode}-${index}`,
            _source: 'manual-group'
          };
          manualBonds.push(bondWithKey);
        }
      });

      // 국고채 그룹화 (2년, 3년, 5년, 10년, 20년, 30년)
      for (const bond of manualBonds.filter(b => b.bondType === 'KTB')) {
        const ktbType = bond.ktbType; // Y02, Y03, Y05, Y10, Y20, Y30
        const typeLabel = `국고채(${getTypeLabel(ktbType)})`;
        const groupId = `KTB-${ktbType}`;

        if (!groupMap.has(groupId)) {
          groupMap.set(groupId, {
            id: groupId,
            label: typeLabel,
            bonds: [],
          });
        }

        groupMap.get(groupId).bonds.push(bond);
      }

      // 통안채 그룹화 - 기간별로 그룹화
      for (const bond of manualBonds.filter(b => b.bondType === 'MSB')) {
        const msbType = bond.msbType;
        let groupId;
        let typeLabel;

        // 통안채 유형에 따른 그룹 분류
        // TD(통당), GTD(구통당), GGTD(구구통당)는 모두 2년물로 분류
        if (['TD', 'GTD', 'GGTD'].includes(msbType)) {
          groupId = 'MSB-2Y';
          typeLabel = '통안채(2년)';
        } else if (['ST', 'GST', 'GGST'].includes(msbType)) {
          // ST(삼통), GST(구삼통), GGST(구구삼통)는 3년물로 분류
          groupId = 'MSB-3Y';
          typeLabel = '통안채(3년)';
        } else {
          // 기타 통안채
          groupId = `MSB-${msbType}`;
          typeLabel = `통안채(${msbType})`;
        }

        if (!groupMap.has(groupId)) {
          groupMap.set(groupId, {
            id: groupId,
            label: typeLabel,
            bonds: [],
          });
        }

        groupMap.get(groupId).bonds.push(bond);
      }

      // 각 그룹 내에서 만기순 정렬 적용
      for (const group of groupMap.values()) {
        group.bonds = sortBondsForDisplay(group.bonds);
      }

      // 그룹 정렬 순서: 국고채 먼저, 그 다음 통안채
      // 각 카테고리 내에서는 기간 순으로 정렬 (연내, 2년, 3년, 5년, 10년, 20년, 30년)
      const sortOrder = {
        'KTB-Y01': 1,
        'KTB-Y02': 2,
        'KTB-Y03': 3,
        'KTB-Y05': 4,
        'KTB-Y10': 5,
        'KTB-Y20': 6,
        'KTB-Y30': 7,
        'MSB-2Y': 8,
        'MSB-3Y': 9,
        'MSB-1Y': 10,
        'MSB-ETC': 11,
      };

      return Array.from(groupMap.values()).sort((a, b) => {
        return (sortOrder[a.id] || 99) - (sortOrder[b.id] || 99);
      });
    });

    // MSB 유형 이름 가져오기
    const getMsbTypeName = type => {
      const msbTypes = {
        TD: '통당',
        GTD: '구통당',
        GGTD: '구구통당',
        ST: '삼통',
        GST: '구삼통',
        GGST: '구구삼통',
        ETC: '기타',
      };
      return msbTypes[type] || type;
    };

    // 그룹 불러오기 함수
    const loadGroup = group => {
      if (!group || !group.bonds) return;

      // 선택된 채권 목록 업데이트
      selectedBondsForGroup.value = [...group.bonds];

      // 현재 그룹을 활성 상태로 표시
      activeGroupIndex.value = savedGroups.value.findIndex(
        g => g.name === group.name
      );

      // 선택된 모든 국고채/통안채 타입 초기화
      selectedKtbTypes.value = [];
      selectedMsbTypes.value = [];

      // 그룹에 포함된 모든 채권 코드 처리
      const bondCodes = [...group.bonds];

      // 채권 코드로 분류
      if (bondCodes.length > 0) {
        // 채권 코드를 국고채와 통안채로 분류
        const ktbCodes = bondCodes.filter(code => code.startsWith('KTB'));
        const msbCodes = bondCodes.filter(code => code.startsWith('MSB'));

        // 국고채와 통안채 코드 설정
        if (ktbCodes.length > 0) {
          selectedBondCodes.value = ktbCodes;
          selectedBondType.value = 'KTB';
        } else if (msbCodes.length > 0) {
          selectedBondCodes.value = msbCodes;
          selectedBondType.value = 'MSB';
        }
      }

      // 선택된 채권 데이터 새로고침
      refreshFilteredBonds();

      // 첫 번째 채권 자동 선택 (존재할 경우)
      if (group.bonds.length > 0) {
        selectBond(group.bonds[0]);
      }
    };

    // refreshFilteredBonds 함수 추가
    const refreshFilteredBonds = () => {
      // 필터링된 국고채와 통안채 목록 갱신
      const allKtbBonds = [];
      const allMsbBonds = [];

      // ktbBonds의 모든 타입에서 bond 객체 추출
      Object.values(ktbBonds.value).forEach(bonds => {
        if (Array.isArray(bonds)) {
          bonds.forEach(bond => {
            if (manuallySelectedBonds.value.includes(bond.code)) {
              allKtbBonds.push(bond);
            }
          });
        }
      });

      // msbBonds에서 bond 객체 추출
      msbBonds.value.forEach(bond => {
        if (manuallySelectedBonds.value.includes(bond.code)) {
          allMsbBonds.push(bond);
        }
      });

      // 그룹 데이터가 업데이트 되었음을 반영
      // 필요한 경우 여기서 추가 로직 구현
    };

    return {
      selectedDate,
      selectedKtbTypes,
      selectedMsbTypes,
      selectedBondCodes,
      ktbBonds,
      msbBonds,
      chatList,
      filteredChatList,
      selectedBondName,
      selectedBond,
      isLoading,
      chatContainer,
      savedGroups,
      selectedBondsForGroup,
      activeGroupIndex,
      sortBy,
      sortDirection,
      hideFragmentTrades,
      hideWithoutPrice,
      filteredKtbTypes,
      filteredMsbTypes,
      displayedBonds,
      selectedBondType,
      allBonds,
      getTypeLabel,
      formatBondName,
      formatTime,
      selectBond,
      toggleSort,
      formatRemainingTerm,
      showSaveModal,
      newGroupName,
      openSaveModal,
      getBondNameByCode,
      getBondTypeByCode,
      getBondYieldByCode,
      toggleSelectedBond,
      isSelectedForGroup,
      getBondBuyPriceByCode,
      getBondSellPriceByCode,
      removeBondFromSelection,
      clearSelectedBonds,
      saveGroup,
      groupedBondTypes,
      getBondsByType,
      removeGroup,
      loadGroup,
      selectAllBonds,
      copyToClipboard,
      showCopyNotification,
      calculateSpread,
      formatSpread,
      switchBondType,
      loadInitialData,
      groupedBondsByType,
      selectedBasketsData,
      basketAsGroups,
      combinedGroupedBonds,
      selectedBasketIds,
      manuallySelectedBonds,
      getMsbTypeName,
      searchBondByCode,
      searchBondInput,
      ktbQuotes,
      loadKtbQuotes,
      calculateYieldSpread,
      formatTradingVolume,
      isGroupedView,
      hideEvaluatedYield,
      // SSE 관련
      sseEventSource,
      lastUpdatedBondCode,
      isSSEConnecting,
      connectSSEStream,
      disconnectSSE,
      initializeSSEConnection,
      handleQuoteUpdate,
      handleChatUpdate,
      hasNewQuote,
      clearNewQuoteAlert,
      // 바스켓 관리 관련
      userBaskets,
      loadUserBaskets,
      getBasketItemCount,
      openCreateBasketModal,
      getSelectedBasketsNames,
      clearAllBasketSelections,
      selectBasket,
      editBasket,
      deleteBasket,
      // 모달 관련
      isEditMode,
      editingBasketId,
      modalSelectedBonds,
      bondSearchQuery,
      allBondsForModal,
      filteredAllBonds,
      addBondToModal,
      removeBondFromModal,
      handleDragStart,
      handleDragEnd,
      handleDrop,
      closeSaveModal,
    };
  },
};
</script>

<style scoped>
.font-mono {
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
}

.buy-indicator {
  position: relative;
}

.sell-indicator {
  position: relative;
}

/* 이모지 표시 문제 해결을 위한 스타일 */
[class*='emoji'],
.emoji-display {
  font-family:
    'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol', 'Noto Color Emoji';
  font-size: 18px;
  line-height: 1;
  vertical-align: middle;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
}
</style>
